import { useState } from 'react';
import { CalendarDays } from 'lucide-react';

import type { CandidateRead, Meeting } from '@/api/schema';
import { Panel } from '@/components/layout/Page';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useCurrentUser } from '@/features/auth/api';
import { useDateFormat } from '@/features/company/api';
import { formatDateTime } from '@/lib/dates';
import { can } from '@/lib/permissions';

import { useCandidateMeetings } from '../api';
import { MEETING_STATUS_LABEL } from '../labels';
import { ScheduleDrawer } from './ScheduleDrawer';
import styles from './CandidateMeetings.module.css';

export function CandidateMeetings({ candidate }: { candidate: CandidateRead }) {
  const { data: user } = useCurrentUser();
  const meetings = useCandidateMeetings(candidate.id);
  const dateFormat = useDateFormat();
  const [editing, setEditing] = useState<Meeting | 'new' | null>(null);
  return (
    <Panel title="Meetings" icon={<CalendarDays size={15} />}>
      {can(user, 'meetings', 'create') ? (
        <Button
          size="sm"
          onClick={() => {
            setEditing('new');
          }}
        >
          Schedule a meeting
        </Button>
      ) : null}
      {meetings.isPending ? (
        <Spinner label="Loading meetings" />
      ) : meetings.isError ? (
        <ErrorState
          message="Meetings could not be loaded."
          onRetry={() => {
            void meetings.refetch();
          }}
        />
      ) : meetings.data.length === 0 ? (
        <p>No meetings scheduled for this candidate.</p>
      ) : (
        <ul className={styles.list}>
          {meetings.data.map((meeting) => (
            <li key={meeting.id}>
              <div>
                <b>{meeting.title}</b>
                <p>
                  {formatDateTime(meeting.scheduled_at, dateFormat)} ·{' '}
                  {MEETING_STATUS_LABEL[meeting.status]}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(meeting);
                }}
              >
                View meeting
              </Button>
            </li>
          ))}
        </ul>
      )}
      {editing ? (
        <ScheduleDrawer
          meeting={editing === 'new' ? null : editing}
          candidateId={candidate.id}
          candidateName={candidate.full_name}
          onClose={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </Panel>
  );
}
